import type {
  KeyIntValStringCollection,
  KeyStringValStringCollection,
  UserResponse,
  UserResponseRuianResponse,
} from '@1webit/cvs-api-scheme';
import { isWoman, vocative } from 'czech-vocative';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { File as FileIcon, FileImage, FileText } from 'lucide-react';
import type { ReactNode } from 'react';
import { Fragment } from 'react';
import { z } from 'zod';

import { fileExtensions } from '@/components/file-dropzone/config';
import { formatBytes } from '@/components/file-dropzone/format-bytes';
import type { Option } from '@/components/ui/select-react/select.interface';
import { DEFAULT_DECIMAL_COUNT, TODAY } from '@/constants';
import { cn, isValueEmpty } from '@/utils';

// TODO: separate these helpers to multiple files

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export const DATE_FORMAT = 'DD/MM/YYYY';

export const formatPhoneNumber = (phoneNumber: string): string => {
  if (phoneNumber.length === 9) {
    return phoneNumber.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3');
  }
  return phoneNumber;
};

export const removeEmptyValues = <T,>(data: Record<string, unknown>): T => {
  const keys = Object.keys(data) as Array<keyof typeof data>;
  const filteredData: Record<string, unknown> = {};
  keys.forEach((key) => {
    if (data[key] !== '' && data[key] !== undefined && data[key] !== null) {
      filteredData[key] = data[key];
    }
  });
  return filteredData as T;
};

export const numberToCurrency = (input: number, decimals = 2) => {
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency: 'CZK',
    maximumFractionDigits: decimals,
  }).format(input);
};

export const preventEmptyNode = (
  value: ReactNode | undefined | null,
  fallback?: string,
): ReactNode => {
  if (isValueEmpty(value)) {
    return fallback ?? '-';
  }
  return value;
};

export interface UserNameData {
  id?: number;
  title?: string;
  firstName: string;
  lastName: string;
  titleAfterName?: string;
  // BasicInfoResponse field names
  userId?: number;
  titleBefore?: string;
  titleAfter?: string;
}
export const userToFullName = (
  user: UserNameData,
  useVocative: boolean = false,
  includeId: boolean = false,
): string => {
  const titleBefore = user.title ?? user.titleBefore;
  const titleAfter = user.titleAfterName ?? user.titleAfter;
  const id = user.id ?? user.userId;
  const title = isValueEmpty(titleBefore) ? '' : `${titleBefore} `;
  const firstName = useVocative
    ? vocative(user.firstName, isWoman(user.firstName)) || ''
    : user.firstName || '';
  const lastName = useVocative
    ? vocative(user.lastName, isWoman(user.firstName)) || ''
    : user.lastName || '';
  const titleAfterName = isValueEmpty(titleAfter) ? '' : ` ${titleAfter}`;

  return `${title}${lastName} ${firstName}${titleAfterName}${includeId && id !== undefined ? ` (${id})` : ''}`.trim();
};

export const getFullName = (firstName: string, lastName: string): string => {
  return `${lastName} ${firstName}`;
};

interface AddressData {
  street?: string | null;
  /** číslo popisné nebo evidenční (podle `so`) */
  cp: string;
  /** číslo orientační */
  co?: string | null;
  /** dodatek (písmeno) čísla orientačního */
  char?: string | null;
  /** typ stavebního objektu – „č.p.“ nebo „č.ev.“ */
  so?: string | null;
  city: string;
  /** název části obce */
  section?: string | null;
  /** název městského obvodu v Praze (např. „Praha 6“) */
  mopName?: string | null;
  zip?: string | null;
}

/**
 * Sestaví adresu v řádcích za sebou oddělených čárkou dle § 6 a přílohy č. 1
 * vyhlášky č. 359/2011 Sb., např. „Studentská 1903/14a, Dejvice, 16000 Praha 6“.
 */
export const ruianToAddress = (ruian: AddressData): string => {
  const isEvidenceNumber = ruian.so?.replace(/\s/g, '').toLowerCase() === 'č.ev.';
  const hasStreet = !isValueEmpty(ruian.street);
  const showSection = !isValueEmpty(ruian.section) && ruian.section !== ruian.city;

  // Před číslem evidenčním se „č.ev.“ uvádí vždy, před číslem popisným „č.p.“ jen bez ulice i části obce
  let houseNumber = ruian.cp;
  if (isEvidenceNumber) {
    houseNumber = `č.ev. ${ruian.cp}`;
  } else if (!hasStreet && !showSection) {
    houseNumber = `č.p. ${ruian.cp}`;
  }

  const lines: string[] = [];
  if (hasStreet) {
    // Číslo orientační včetně dodatku se uvádí za číslo popisné, oddělené lomítkem
    const orientationNumber = isValueEmpty(ruian.co) ? '' : `/${ruian.co}${ruian.char ?? ''}`;
    lines.push(`${ruian.street} ${houseNumber}${orientationNumber}`);
    if (showSection) {
      lines.push(ruian.section!);
    }
  } else {
    // Nepojmenovaná ulice – číslo se uvede za názvem části obce
    lines.push(showSection ? `${ruian.section} ${houseNumber}` : houseNumber);
  }

  // V Praze se za názvem obce uvede číslo obvodu
  const city = isValueEmpty(ruian.mopName) ? ruian.city : ruian.mopName;
  lines.push(isValueEmpty(ruian.zip) ? city : `${ruian.zip} ${city}`);

  return lines.join(', ');
};

export const userToAddress = (user: UserResponse): string => {
  if (!isValueEmpty(user.ruianResponse)) {
    return ruianToAddress(user.ruianResponse);
  }

  return `${user.street}, ${user.postalCode} ${user.city}`;
};

export const requestTypes: { [key: number]: string } = {
  2: 'member-registration',
  9: 'registration-renewal',
  11: 'cancel-registration',
  6: 'new-card',
  33: 'change-photo',
  34: 'change-personal-info',
  36: 'extend-card',
  20: 'membership-payment',
  4: 'championship-application',
  12: 'regional-application',
  32: 'change-hall',
  17: 'change-match-term',
  24: 'change-match-term',
  38: 'change-tournament-term',
  25: 'penalty',
  1: 'report-transfer',
  7: 'report-hosting',
  13: 'cancel-hosting',
  26: 'transfer-to-team',
  35: 'substitute-hall',
  37: 'extend-hosting',
  19: 'penalty-beach',
  22: 'beach-application',
  23: 'beach-cancel',
  27: 'beach-hosting',
};

export const handleNumericInputChange
  = <T extends string | number>(field: {
    onChange: (value: T) => void;
    value: T;
  }) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;

      if (!/^\d*$/.test(newValue)) {
        return;
      }

      field.onChange(newValue === '' ? ('' as T) : (newValue as T));
    };

export const qualificationTypes: Record<string, string> = {
  rozhodci: 'Rozhodčí',
  trener: 'Trenér',
  rozh_beach: 'Rozhodčí beachvolejbalu',
  tren_beach: 'Rozhodčí beachvolejbalu',
};

export const booleanToString = (value: boolean) => (value ? 'Ano' : 'Ne');

export const clearable = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess(value => value ?? undefined, schema);

export const numberOptional = (typeError: string = 'Toto pole musí být číslo') =>
  z.union([z.string(), z.number()]).nullish().refine(
    (value) => {
      if (typeof value !== 'number' && !value) {
        return true;
      }
      const numberValue = Number(value);
      return !Number.isNaN(numberValue);
    },
    { message: typeError },
  ).transform(value =>
    (typeof value !== 'number' && !value) ? undefined : Number(value),
  );

export const numberStrict = (
  required_error: string = 'Toto pole je povinné',
  typeError: string = 'Toto pole musí být celé číslo',
  minValue?: number,
) =>
  z.union([z.number({ required_error }), z.string({ required_error }).regex(/^-?\d+$/, typeError).transform(Number)])
    .refine(num => minValue === undefined || num >= minValue, {
      message: `Minimální hodnota je ${minValue}`,
    });

export const numberStrictFloatPositive = (
  requiredError = 'Toto pole je povinné',
  typeError = 'Toto pole musí být kladné číslo',
) =>
  z.union([
    z
      .number({ invalid_type_error: typeError, required_error: requiredError })
      .refine(num => num >= 0, { message: 'Číslo nesmí být záporné' }),

    z
      .string({ required_error: requiredError })
      .regex(/^(?:\d+(?:\.\d+)?|\.\d+)$/, { message: typeError })
      .transform(val => Number(val))
      .refine(num => num >= 0, { message: 'Číslo nesmí být záporné' }),
  ]);

export const numberStrictFloat = (
  requiredError = 'Toto pole je povinné',
  typeError = 'Toto pole musí být číslo',
) =>
  z.union([
    z.number({ invalid_type_error: typeError, required_error: requiredError }),
    z
      .string({ required_error: requiredError })
      .min(1, { message: requiredError })
      .regex(/^-?(?:\d+(?:\.\d+)?|\.\d+)$/, { message: typeError })
      .transform(val => Number(val)),
  ]);

export const numberStrictFloatPositiveOptional = (
  typeError: string = 'Toto pole musí být kladné číslo',
) =>
  z
    .union([z.number(), z.string()])
    .optional()
    .refine(
      val =>
        val === undefined
        || val === ''
        || typeof val === 'number'
        || /^(?:\d+(?:\.\d+)?|\.\d+)$/.test(val),
      { message: typeError },
    )
    .transform((val) => {
      if (val === undefined) {
        return undefined;
      }
      if (val === '') {
        return null;
      }

      return Number(val);
    })
    .refine(
      num => num === undefined || num === null || num >= 0,
      { message: 'Číslo nesmí být záporné' },
    );

export const numberStrictOptional = (typeError: string = 'Toto pole musí být číslo') =>
  z
    .union([z.string().regex(/^\d+$/, typeError), z.literal(''), z.null(), z.undefined(), z.number()])
    .transform((value) => {
      if (value === undefined
      ) {
        return value;
      }
      if (value === '') {
        return 0;
      }
      if (value === null) {
        return 0;
      }
      return Number(value);
    });

export const numberStrictNoTransform = (requiredError: string = 'Toto pole je povinné', typeError: string = 'Toto pole musí být číslo') =>
  z.string({
    required_error: requiredError,
  }).regex(/^\d+$/, typeError);
export const numberStrictNoTransformOptional = (
  typeError: string = 'Toto pole musí být číslo',
) =>
  z.union([
    z.string().regex(/^\d+$/, typeError),
    z.literal(''),
    z.undefined(),
  ]);

export const fileSchema = (
  maxSize: number,
  acceptedTypes: string[],
  requiredError: string = 'Soubor je povinný',
) => {
  const extensions = acceptedTypes.flatMap(type => fileExtensions[type as keyof typeof fileExtensions] ?? type);

  return z
    .instanceof(File, { message: requiredError })
    .refine(file => file.size <= maxSize, `Maximální velikost souboru je ${formatBytes(maxSize)}.`)
    .refine(file => acceptedTypes.includes(file.type), `Podporované formáty: ${extensions.join(', ')}.`);
};

export const checkEmail = (email: string) => /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/.test(email);

export const formatNumber = (
  number: number | string,
  decimalCount: number = DEFAULT_DECIMAL_COUNT,
  fixedDecimals: boolean = false,
) => {
  let formattedNumber = Number(number);

  if (fixedDecimals || formattedNumber % 1 !== 0) {
    formattedNumber = Number.parseFloat(formattedNumber.toFixed(decimalCount));
  }

  let numberString = formattedNumber.toString();

  if (fixedDecimals) {
    const parts = numberString.split('.');
    if (parts.length === 1) {
      numberString += `.${'0'.repeat(decimalCount)}`;
    } else {
      const currentDecimals = parts[1]?.length || 0;
      if (currentDecimals < decimalCount) {
        numberString += '0'.repeat(decimalCount - currentDecimals);
      }
    }
  }

  if (formattedNumber >= 1000 || formattedNumber <= -1000) {
    return numberString
      .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
      .replace('.', ',');
  }

  return numberString.replace('.', ',');
};

export const filterUndefinedValuesNullToZero = <T extends Record<string, string | number | boolean | null | undefined>>(
  obj: T,
): Partial<T> => {
  return Object.fromEntries(
    Object.entries(obj).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc.push([key, value === null ? 0 : value]);
      }
      return acc;
    }, [] as (string | number | boolean)[][]),
  ) as Partial<T>;
};

/**
 * Compares new and old values and returns only the changed ones
 * @param newValues - New values
 * @param oldValues - Old values
 */
export const getChangedValues = <T extends object>(newValues: T, oldValues: T): Partial<T> => {
  const changedValues: Partial<T> = {};

  for (const key in newValues) {
    if (newValues[key] !== oldValues[key]) {
      changedValues[key] = newValues[key];
    }
  }

  return changedValues;
};

/**
 * Converts a file type to an icon
 * @param type - The file type
 * @param classname - The class name for the icon
 * @returns The icon component
 */
export const fileTypeToIcon = (type: string, classname?: string): ReactNode => {
  const iconClass = cn('h-6', classname);

  if (type.startsWith('image/')) {
    return <FileImage className={iconClass} />;
  } else if (type.endsWith('/pdf')) {
    return <FileText className={iconClass} />;
  } else {
    return <FileIcon className={iconClass} />;
  }
};

export const keyValListToOptions = <T extends string | number>(lists?: T extends string ? KeyStringValStringCollection : KeyIntValStringCollection): Option<T>[] => {
  if (lists === undefined) {
    return [];
  }

  return lists.map(item => ({
    value: item.key as T,
    label: item.val,
  }));
};
export const ruianToHumanReadable = (
  ruian?: UserResponseRuianResponse,
): string => {
  if (ruian === null || ruian === undefined) {
    return '';
  }
  return `${ruian.street} ${ruian.cp}, ${ruian.city} ${ruian.zip}`;
};

/**
 * Creates an array of year options to use in select
 * @param firstYear - the oldest year to show
 * @param lastYear - the last year to show in options
 */
export const getYearSelectOptions = (firstYear = 1970, lastYear = TODAY.getFullYear()): Option[] => {
  return Array.from(
    { length: lastYear - firstYear + 1 },
    (_, i) => {
      const year = lastYear - i;
      return {
        value: year,
        label: year.toString(),
      };
    },
  );
};

export const apiDateTimeRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])T([01]\d|2[0-3]):[0-5]\d:[0-5]\d([+-](0\d|1\d|2[0-3]):[0-5]\d)$/;

export const formatBeachEvaluation = (input: string): ReactNode => {
  if (isValueEmpty(input)) {
    return null;
  }

  return (
    <>
      {input.split(':').map((part, idx) => (
        <Fragment key={idx}>
          {idx !== 0 && <>&rArr;</>}
          {part}
        </Fragment>
      ))}
      ;
    </>
  );
};

export const safelyParseNumber = (input: string, float = false): number | undefined => {
  const fun = float ? Number.parseFloat : Number.parseInt;
  const num = fun(input);
  if (Number.isNaN(num)) {
    return undefined;
  }
  return num;
};
